import * as React from "react";
import { cn } from "../../lib/utils";
import { Textarea } from "./textarea";

const DEFAULT_MAX_LINES = 5;

/**
 * Grows a textarea with wrapped text up to maxLines, then enables a vertical scrollbar.
 * @param {HTMLTextAreaElement | null} el
 * @param {number} [maxLines]
 */
function adjustExpandingTextareaHeight(el, maxLines = DEFAULT_MAX_LINES) {
  if (!el) {
    return;
  }
  const style = window.getComputedStyle(el);
  const fontSize = parseFloat(style.fontSize);
  let lineHeight = parseFloat(style.lineHeight);
  if (!Number.isFinite(lineHeight) || lineHeight <= 0) {
    lineHeight = Number.isFinite(fontSize) && fontSize > 0 ? fontSize * 1.5 : 0;
  }
  if (!Number.isFinite(lineHeight) || lineHeight <= 0) {
    el.style.height = "auto";
    return;
  }
  const paddingY = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
  const borderY = parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth);
  const maxHeight = lineHeight * maxLines + paddingY + borderY;
  el.style.height = "auto";
  const scrollHeight = el.scrollHeight;
  el.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
  el.style.overflowY = scrollHeight > maxHeight ? "auto" : "hidden";
}

/**
 * Assign one or more React refs to the same node.
 * @param {HTMLTextAreaElement | null} node
 * @param {...(React.Ref<HTMLTextAreaElement> | undefined)} refs
 */
function assignRefs(node, ...refs) {
  for (const ref of refs) {
    if (!ref) {
      continue;
    }
    if (typeof ref === "function") {
      ref(node);
    } else {
      ref.current = node;
    }
  }
}

/**
 * Textarea that expands with wrapped text up to {@link DEFAULT_MAX_LINES} lines,
 * then scrolls. Enter (without Shift) calls {@code onSubmitEnter} when provided;
 * Shift+Enter still inserts a newline.
 *
 * @param {Object} props
 * @param {string} [props.className]
 * @param {string} [props.value]
 * @param {number} [props.maxLines]
 * @param {() => void} [props.onSubmitEnter]
 * @param {React.KeyboardEventHandler<HTMLTextAreaElement>} [props.onKeyDown]
 * @param {React.ChangeEventHandler<HTMLTextAreaElement>} [props.onChange]
 * @returns {JSX.Element}
 */
const ExpandingTextarea = React.forwardRef(
  (
    {
      className,
      value,
      maxLines = DEFAULT_MAX_LINES,
      onSubmitEnter,
      onKeyDown,
      onChange,
      ...props
    },
    forwardedRef
  ) => {
    const localRef = React.useRef(null);

    React.useLayoutEffect(() => {
      adjustExpandingTextareaHeight(localRef.current, maxLines);
    }, [value, maxLines]);

    const handleKeyDown = (e) => {
      onKeyDown?.(e);
      if (e.defaultPrevented) {
        return;
      }
      if (e.key === "Enter" && !e.shiftKey && onSubmitEnter) {
        e.preventDefault();
        onSubmitEnter();
      }
    };

    const handleChange = (e) => {
      onChange?.(e);
      // Resize immediately so paste/IME updates feel snappy before the value effect.
      adjustExpandingTextareaHeight(e.target, maxLines);
    };

    return (
      <Textarea
        {...props}
        ref={(node) => assignRefs(node, localRef, forwardedRef)}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        rows={props.rows ?? 1}
        className={cn(
          "expanding-textarea-scrollbar min-h-0 max-h-none resize-none overflow-hidden",
          className
        )}
      />
    );
  }
);
ExpandingTextarea.displayName = "ExpandingTextarea";

export { ExpandingTextarea, adjustExpandingTextareaHeight, DEFAULT_MAX_LINES };
